<template>
    <TransitionRoot :show="isOpen" as="template">
        <Dialog class="relative z-[999999]" @close="onClose">
            <TransitionChild
                enter="duration-300 ease-out"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-grind/60"></div>
            </TransitionChild>

            <div class="fixed bottom-0 right-0 top-0 flex w-full max-w-[500px]">
                <TransitionChild
                    enter="duration-300 ease-out"
                    enter-from="translate-x-full"
                    enter-to="translate-x-0"
                    leave="duration-200 ease-in"
                    leave-from="translate-x-0"
                    leave-to="translate-x-full"
                    class="flex w-full bg-white shadow-card"
                >
                    <DialogPanel
                        class="max-h-[100vh] w-full overflow-y-auto overflow-x-hidden p-6"
                        data-cy="add-to-cart-flyout"
                    >
                        <DialogTitle class="relative" as="h3">
                            Options

                            <button
                                class="absolute right-0 top-0 transition-colors hover:text-brew focus:outline-none"
                                data-cy="add-to-cart-flyout-close"
                                @click.prevent="onClose"
                            >
                                <Icon
                                    name="close"
                                    class="text-[24px] sm:text-[30px]"
                                />
                            </button>
                        </DialogTitle>

                        <div
                            class="my-4 flex flex-col divide-y divide-grind-200"
                        >
                            You can always change these later.
                        </div>

                        <Alert v-if="state.showError" type="error" class="mb-4">
                            Make the following selections to continue:
                            <ul class="ml-4 list-disc">
                                <li>Missing valve selection</li>
                            </ul>
                        </Alert>

                        <div class="mb-8 flex flex-col gap-5">
                            <div class="my-2 flex">
                                <div
                                    class="mr-4 text-left font-semibold"
                                    :class="
                                        state.showError
                                            ? 'text-error'
                                            : 'text-grind'
                                    "
                                >
                                    Valve
                                </div>
                                <div class="group relative flex">
                                    <button
                                        aria-label="Learn More"
                                        class="mr-2"
                                        @click="modalRef?.open()"
                                    >
                                        <Icon
                                            name="question-circle"
                                            class="text-xl"
                                        />
                                    </button>
                                    <Tooltip
                                        class="absolute left-[50%] top-8 z-50 opacity-0 transition group-hover:opacity-100"
                                        position="bottom"
                                    >
                                        Details
                                    </Tooltip>
                                </div>
                            </div>
                            <DesignLabOptionsGroup
                                :error="state.showError"
                                :selected="
                                    state.valveSelected === null
                                        ? null
                                        : state.valveSelected
                                          ? standUpPouchWithValve
                                          : standUpPouch
                                "
                                :options="standUpPouchOptions"
                                @select-option="updateValveSelection"
                            />
                        </div>

                        <div class="flex flex-col gap-5">
                            <button class="btn-primary" @click="startDesigning">
                                Start Designing
                            </button>
                        </div>

                        <Modal ref="modalRef" :show-action-buttons="true">
                            <template #title> Degassing Valve </template>
                            <div class="mb-4">
                                Our one-way degassing button valve vents the CO2
                                that is naturally released from freshly roasted
                                coffee beans. Without a valve, the release of
                                gas could rupture the coffee bag.
                            </div>
                            <div class="mb-6">
                                Our valve will not infringe on the design/print
                                of your bag.
                            </div>
                            <div class="mb-4 text-lg font-bold">
                                Choose this option if
                            </div>
                            <ul class="mb-6 ml-4 list-disc">
                                <li>
                                    You are packaging freshly roasted coffee
                                    beans
                                </li>
                                <li>
                                    You are bagging any other material that
                                    naturally releases gas after packaging
                                </li>
                            </ul>
                            <div class="mb-4 text-lg font-bold">
                                Valve Placement
                            </div>
                            <ul class="mb-4 ml-4 list-disc">
                                <li>
                                    Valve is placed 3" from the top of the bag
                                </li>
                            </ul>
                        </Modal>
                    </DialogPanel>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup lang="ts">
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from '@headlessui/vue';

import {
    standUpPouch,
    standUpPouchWithValve,
} from '#shared/data/designLab/productIds';
import type { OptionsCardType } from '#shared/types/design-lab/OptionsCardType';
import Modal from '~/components/Modal.vue';
import DesignLabOptionsGroup from '~/components/design-lab/components/drawers/components/DesignLabOptionsGroup.vue';

const modalRef = ref<InstanceType<typeof Modal> | null>(null);

const state = reactive<{
    isOpen: boolean;
    method: string;
    theme: string;
    showError: boolean;
    valveSelected: boolean | null;
}>({
    isOpen: false,
    method: '',
    theme: '',
    showError: false,
    valveSelected: null,
});

const standUpPouchOptions: OptionsCardType[] = [
    {
        value: standUpPouchWithValve,
        topText: 'Degassing Valve',
        middleText: '+$0.08 per bag',
        bottomText:
            'A degassing valve is required for packaging coffee to make sure your beans stay fresh.',
        img: {
            src: '/images/design-lab/degassing-valve.png',
            alt: 'Coffee Bag Label',
        },
        isBackgroundImg: true,
    },
    {
        value: standUpPouch,
        topText: 'No Degassing Valve',
        isBackgroundImg: true,
    },
];

const open = (method: string, theme: string) => {
    state.isOpen = true;
    state.method = method;
    state.theme = theme;
    state.valveSelected = null;
};

const onClose = () => {
    state.isOpen = false;
};
const { isOpen } = toRefs(state);

if (!useNuxtApp().$supOptionsFlyout) {
    useNuxtApp().provide(
        'supOptionsFlyout',
        (method: string, theme: string) => {
            open(method, theme);
        },
    );
}

const updateValveSelection = (valveProductId: number) => {
    state.showError = false;
    state.valveSelected = valveProductId === standUpPouchWithValve;
};

const startDesigning = () => {
    if (state.valveSelected === null) {
        state.showError = true;
    } else {
        state.isOpen = false;
        const baseUrl =
            '/roastar-design-lab#product=' +
            (state.valveSelected ? standUpPouchWithValve : standUpPouch) +
            '&method=' +
            state.method;

        const themeParam = state.theme ? '&theme=' + state.theme : '';

        navigateTo(baseUrl + themeParam, {
            external: true,
        });
    }
};

defineExpose({ open });
</script>
